import { useMemo } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { planApi } from '@src/apis/admin';
import type { PlanHistoryResponseModelPaginatedResponse, PlanHistoryResponseModel } from '@uniquegood/realworld-admin-interface';

export interface PlanHistoryParams {
    page: number;
    limit: number;
    eventId?: string;
}

export interface PlanHistoryResponse {
    items: PlanHistoryResponseModel[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

type PlanHistoryQueryKey = readonly ['plan-history', string, PlanHistoryParams];

export interface UsePlanHistoryOptions {
    /**
     * React Query 옵션을 덮어씁니다.
     * `queryKey`와 `queryFn`은 내부에서 관리하므로 제외했습니다.
     */
    queryOptions?: Omit<
        UseQueryOptions<PlanHistoryResponse, Error, PlanHistoryResponse, PlanHistoryQueryKey>,
        'queryKey' | 'queryFn'
    >;
}

/**
 * 요금제 내역 조회 훅
 *
 * @description
 * - 채널 ID와 페이지네이션 파라미터를 받아 요금제 내역을 조회합니다.
 * - PlanApi.getPlanHistory를 사용하여 실제 API를 호출합니다.
 * - x-rwd-api-version은 '1.0'을 사용합니다.
 */
export const usePlanHistory = (
    channelId: string,
    params: PlanHistoryParams,
    options: UsePlanHistoryOptions = {}
): UseQueryResult<PlanHistoryResponse> => {
    const { queryOptions } = options;
    const { staleTime, ...restQueryOptions } = queryOptions ?? {};

    const queryKey = useMemo<PlanHistoryQueryKey>(
        () => ['plan-history', channelId, params] as const,
        [channelId, params]
    );

    return useQuery<PlanHistoryResponse, Error, PlanHistoryResponse, PlanHistoryQueryKey>({
        queryKey,
        queryFn: async () => {
            const { data } = await planApi.getPlanHistory(
                channelId,
                params.page,
                params.limit,
                params.eventId || undefined,
                '1.0' // x-rwd-api-version
            );
            
            // API 응답 타입: PlanHistoryResponseModelPaginatedResponseApiSuccessResponseModel
            // data.data가 PlanHistoryResponseModelPaginatedResponse
            const paginatedResponse: PlanHistoryResponseModelPaginatedResponse = data.data;
            const items: PlanHistoryResponseModel[] = paginatedResponse?.data ?? [];
            
            return {
                items,
                total: paginatedResponse?.count ?? 0,
                page: paginatedResponse?.pageIndex ?? params.page,
                limit: params.limit,
                totalPages: paginatedResponse?.totalPages ?? Math.ceil((paginatedResponse?.count ?? 0) / params.limit),
            };
        },
        enabled: !!channelId,
        staleTime: staleTime ?? 30 * 1000,
        refetchOnMount: 'always',
        ...restQueryOptions,
    });
};
